import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  BookOpen, 
  Video, 
  Users, 
  CreditCard, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  Home,
  GraduationCap,
  FileText,
  ClipboardList
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lvc-live-classes/artifacts/op54e7uq_WhatsApp%20Image%202026-04-08%20at%2013.06.26.jpeg";

const adminLinks = [
  { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/users', label: 'Usuarios', icon: Users },
  { href: '/admin/courses', label: 'Módulos', icon: BookOpen },
  { href: '/admin/enrollments', label: 'Inscripciones', icon: GraduationCap },
  { href: '/admin/payments', label: 'Pagos', icon: CreditCard },
  { href: '/teacher/grades', label: 'Notas y Asistencia', icon: ClipboardList },
];

const teacherLinks = [
  { href: '/teacher', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/teacher/courses', label: 'Mis Cursos', icon: BookOpen },
  { href: '/teacher/classes', label: 'Clases en Vivo', icon: Video },
  { href: '/teacher/grades', label: 'Notas y Asistencia', icon: ClipboardList },
];

const studentLinks = [
  { href: '/student', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/student/courses', label: 'Explorar Cursos', icon: BookOpen },
  { href: '/student/my-courses', label: 'Mis Cursos', icon: GraduationCap },
  { href: '/student/payments', label: 'Mis Pagos', icon: CreditCard },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getLinks = () => {
    if (user?.role === 'admin') return adminLinks;
    if (user?.role === 'teacher') return teacherLinks;
    return studentLinks;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const links = getLinks();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Mobile header */}
      <header className="lg:hidden sticky top-0 z-50 bg-[#FDFBF7]/80 backdrop-blur-md border-b border-[#E5DFD3]">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA] rounded-md"
          >
            <Menu className="h-6 w-6" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="LVC" className="h-8 w-8 rounded-full object-cover" />
            <span className="font-heading text-lg font-semibold text-[#8B1538]">LVC Misiones</span>
          </Link>
          <div className="w-10" />
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#FDFBF7] border-r border-[#E5DFD3] animate-fadeIn">
            <div className="flex items-center justify-between p-4 border-b border-[#E5DFD3]">
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="LVC" className="h-10 w-10 rounded-full object-cover" />
                <span className="font-heading font-semibold text-[#8B1538]">LVC Misiones</span>
              </Link>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA] rounded-md"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="p-4 space-y-1">
              {links.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors sidebar-item ${
                    location.pathname === link.href
                      ? 'nav-active'
                      : 'text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA]'
                  }`}
                >
                  <link.icon className="h-5 w-5" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex lg:flex-col w-64 min-h-screen border-r border-[#E5DFD3] bg-[#FDFBF7] sticky top-0">
          <div className="p-6 border-b border-[#E5DFD3]">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="LVC" className="h-12 w-12 rounded-full object-cover" />
              <div>
                <span className="font-heading text-lg font-semibold text-[#8B1538] block">LVC Misiones</span>
                <span className="text-xs text-[#737373] capitalize">{user?.role}</span>
              </div>
            </Link>
          </div>
          
          <nav className="flex-1 p-4 space-y-1">
            {links.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors sidebar-item ${
                  location.pathname === link.href
                    ? 'nav-active'
                    : 'text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA]'
                }`}
              >
                <link.icon className="h-5 w-5" />
                <span className="font-medium">{link.label}</span>
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-[#E5DFD3]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  data-testid="user-menu-btn"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[#4A4A4A] hover:text-[#1A1A1A] hover:bg-[#F5F2EA] transition-colors"
                >
                  <div className="h-8 w-8 rounded-full avatar-placeholder flex items-center justify-center text-white font-medium">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-[#1A1A1A] truncate">{user?.name}</p>
                    <p className="text-xs text-[#737373] truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/" className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    <span>Ir al Inicio</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/manual" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>Manual de Usuario</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="text-[#9B2226] focus:text-[#9B2226]"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  <span>Cerrar Sesión</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
}
